import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ArticlesSection = ({ activeTab, userId }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [reservedTopics, setReservedTopics] = useState([]);
  const [blogDrafts, setBlogDrafts] = useState([]);
  const [likedArticles, setLikedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrders, setSortOrders] = useState({});
  const [showSortMessage, setShowSortMessage] = useState(null);

  const getSortOrder = (tab) => sortOrders[tab] || 'recent';
  const toggleSort = (tab) => {
    const newOrder = getSortOrder(tab) === 'recent' ? 'oldest' : 'recent';
    setSortOrders(prev => ({ ...prev, [tab]: newOrder }));
    setShowSortMessage(tab);
    setTimeout(() => setShowSortMessage(null), 1500);
  };

  const sortByDate = (items, tab, dateField = 'createdAt') => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a[dateField] || a.createdAt);
      const dateB = new Date(b[dateField] || b.createdAt);
      return getSortOrder(tab) === 'recent' ? dateB - dateA : dateA - dateB;
    });
  };

  const renderSortButton = (tab) => (
    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
      {showSortMessage === tab && (
        <span style={{ fontSize: '0.8rem', color: '#6B7280', animation: 'fadeIn 0.2s ease-out' }}>
          {getSortOrder(tab) === 'recent' ? 'Most recent' : 'Oldest first'}
        </span>
      )}
      <button
        onClick={() => toggleSort(tab)}
        title={getSortOrder(tab) === 'recent' ? 'Showing most recent' : 'Showing oldest first'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          color: '#9CA3AF', display: 'flex', alignItems: 'center', transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
        onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
      >
        <i className={`bi ${getSortOrder(tab) === 'recent' ? 'bi-sort-down' : 'bi-sort-up'}`} style={{ fontSize: '1.1rem' }}></i>
      </button>
    </div>
  );

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  const getCurrentTab = () => {
    const tabPart = activeTab.split('-')[1];
    return tabPart || 'drafts';
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, userId]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const tab = getCurrentTab();

      if (tab === 'drafts' || tab === 'submitted') {
        const res = await fetch(`${API_BASE}/topics/user/my-topics`, { headers });
        const data = await res.json();
        if (data.success && data.data) {
          // API returns { reservedTopics: [...], drafts: [...], pending: [...] }
          setReservedTopics(Array.isArray(data.data.reservedTopics) ? data.data.reservedTopics : []);
          setBlogDrafts(Array.isArray(data.data.drafts) ? data.data.drafts : []);
        } else {
          setReservedTopics([]);
          setBlogDrafts([]);
        }
      }

      if (tab === 'published' || tab === 'submitted') {
        const res = await fetch(`${API_BASE}/blogs/user/${userId}`, { headers });
        const data = await res.json();
        if (data.success) {
          setArticles(Array.isArray(data.data) ? data.data : []);
        } else {
          setArticles([]);
        }
      }

      if (tab === 'liked') {
        const res = await fetch(`${API_BASE}/blogs/favorites`, { headers });
        const data = await res.json();
        if (data.success) {
          setLikedArticles(Array.isArray(data.data) ? data.data : []);
        } else {
          setLikedArticles([]);
        }
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const releaseTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to release this topic? Your draft will be lost.')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/topics/${topicId}/release`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error releasing topic:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Helper to safely get article title (handles both string and object {_id, name} format)
  const getArticleTitle = (article) => {
    if (article.articleTitle) return article.articleTitle;
    if (typeof article.title === 'string') return article.title;
    if (article.title && typeof article.title === 'object') return article.title.name || 'Untitled';
    return 'Untitled';
  };

  const renderTabContent = () => {
    const tab = getCurrentTab();

    if (loading) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      );
    }

    switch (tab) {
      case 'drafts':
        // Combine reserved topics and blog drafts, avoiding duplicates
        // A topic appears in reservedTopics AND blogDrafts when user has started writing
        // We only show one entry per topic, preferring the draft with content
        const reservedTopicIds = new Set(reservedTopics.map(t => t._id));
        const uniqueDrafts = blogDrafts.filter(draft => !reservedTopicIds.has(draft.topic?._id));
        const allDrafts = [
          ...reservedTopics.map(topic => ({ ...topic, type: 'reserved' })),
          ...uniqueDrafts.map(draft => ({ ...draft, type: 'draft' }))
        ];

        return (
          <div className="articles-list">
            {allDrafts.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-file-earmark-text"></i>
                <h3>No drafts yet</h3>
                <p>Reserve a topic from the catalogue to start writing!</p>
                <button className="primary-btn" onClick={() => navigate('/catalogue')}>
                  Browse Topics
                </button>
              </div>
            ) : (
              allDrafts.map((item) => (
                <div key={item._id} className="article-card">
                  <div className="article-info">
                    <h4>{item.articleTitle || item.name || item.topic?.name || item.title}</h4>
                    <div className="article-meta">
                      <span><i className="bi bi-folder"></i> {item.category || item.topic?.category || 'General'}</span>
                      <span><i className="bi bi-calendar"></i> {item.type === 'reserved' ? 'Reserved' : 'Last edited'} {formatDate(item.reservedAt || item.updatedAt)}</span>
                      {(item.wordCount > 0 || item.content) && (
                        <span><i className="bi bi-file-text"></i> {item.wordCount || item.content?.split(/\s+/).length || 0} words</span>
                      )}
                    </div>
                  </div>
                  <div className="article-actions">
                    <button
                      className="action-btn primary"
                      onClick={() => {
                        const topicIdForWrite = item.type === 'reserved' ? item._id : item.topic?._id;
                        if (topicIdForWrite) {
                          navigate(`/write?topic=${topicIdForWrite}`);
                        } else {
                          navigate('/catalogue');
                        }
                      }}
                    >
                      <i className="bi bi-pencil"></i> Continue Writing
                    </button>
                    <button className="action-btn secondary" onClick={() => releaseTopic(item.topic?._id || item._id)}>
                      <i className="bi bi-x-circle"></i> Release
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'submitted':
        const safeArticles = Array.isArray(articles) ? articles : [];
        // Include both pending and needs_revision articles
        const submittedArticles = safeArticles.filter(a =>
          a.status === 'pending' || a.status === 'needs_revision'
        );
        return (
          <div className="articles-list">
            {submittedArticles.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-send"></i>
                <h3>No pending submissions</h3>
                <p>Articles you submit will appear here while awaiting review.</p>
              </div>
            ) : (
              submittedArticles.map((article) => (
                <div key={article._id} className={`article-card ${article.status === 'needs_revision' ? 'needs-revision' : 'pending'}`}>
                  <div className="article-info">
                    {article.status === 'needs_revision' ? (
                      <span className="status-badge revision">Revision Requested</span>
                    ) : (
                      <span className="status-badge pending">Pending Review</span>
                    )}
                    <h4>{getArticleTitle(article)}</h4>
                    {article.status === 'needs_revision' && article.revisionComments && (
                      <div className="revision-feedback">
                        <i className="bi bi-chat-left-text"></i>
                        <span>{article.revisionComments}</span>
                      </div>
                    )}
                    <div className="article-meta">
                      <span><i className="bi bi-calendar"></i> Submitted {formatDate(article.createdAt)}</span>
                    </div>
                  </div>
                  {article.status === 'needs_revision' && (
                    <div className="article-actions">
                      <button
                        className="action-btn primary"
                        onClick={() => {
                          const topicId = article.topic?._id || article.topic || article.title?._id || article.title;
                          if (topicId) {
                            navigate(`/write?topic=${topicId}`);
                          } else {
                            console.error('No topic ID found for article revision', article);
                          }
                        }}
                      >
                        <i className="bi bi-pencil"></i> Edit Article
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        );

      case 'published':
        const safeArticlesPublished = Array.isArray(articles) ? articles : [];
        const publishedArticles = sortByDate(
          safeArticlesPublished.filter(a => a.status === 'approved'),
          'published', 'updatedAt'
        );
        return (
          <div className="articles-list">
            {publishedArticles.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-journal-check"></i>
                <h3>No published articles yet</h3>
                <p>Once your articles are approved, they'll appear here.</p>
              </div>
            ) : (
              <>
                {renderSortButton('published')}
                {publishedArticles.map((article) => (
                  <div key={article._id} className="article-card published">
                    <div className="article-info">
                      <span className="status-badge published">Published</span>
                      <h4>{getArticleTitle(article)}</h4>
                      <div className="article-meta">
                        <span><i className="bi bi-calendar"></i> Published {formatDate(article.updatedAt || article.createdAt)}</span>
                        <span><i className="bi bi-heart"></i> {article.likedBy?.length || 0} likes</span>
                      </div>
                    </div>
                    <div className="article-actions">
                      <button className="action-btn primary" onClick={() => navigate(`/blog/${article._id}`)}>
                        <i className="bi bi-eye"></i> View Article
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );

      case 'liked':
        const safeLikedArticles = Array.isArray(likedArticles) ? likedArticles : [];
        const sortedLikedArticles = sortByDate(safeLikedArticles, 'liked');
        return (
          <div className="articles-list">
            {safeLikedArticles.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-heart"></i>
                <h3>No liked articles yet</h3>
                <p>Articles you like will be saved here for easy access.</p>
                <button className="primary-btn" onClick={() => navigate('/blog')}>
                  Browse Articles
                </button>
              </div>
            ) : (
              <>
                {renderSortButton('liked')}
                {sortedLikedArticles.map((article) => (
                  <div key={article._id} className="article-card">
                    <div className="article-info">
                      <h4>{getArticleTitle(article)}</h4>
                      <div className="article-meta">
                        <span><i className="bi bi-person"></i> {article.author?.fullName || 'Unknown'}</span>
                        <span><i className="bi bi-calendar"></i> {formatDate(article.createdAt)}</span>
                      </div>
                    </div>
                    <div className="article-actions">
                      <button className="action-btn primary" onClick={() => navigate(`/blog/${article._id}`)}>
                        <i className="bi bi-book"></i> Read
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getTabTitle = () => {
    const tab = getCurrentTab();
    const titles = {
      drafts: 'My Drafts',
      submitted: 'Submitted Articles',
      published: 'Published Articles',
      liked: 'Liked',
    };
    return titles[tab] || 'My Articles';
  };

  const getTabDescription = () => {
    const tab = getCurrentTab();
    const descriptions = {
      drafts: 'Topics you\'ve reserved and are working on',
      submitted: 'Articles awaiting admin review',
      published: 'Your approved and published articles',
      liked: 'Articles you\'ve saved as favorites',
    };
    return descriptions[tab] || '';
  };

  return (
    <div className="section-content articles-section">
      <div className="section-header">
        <h1>{getTabTitle()}</h1>
        <p className="section-subtitle">{getTabDescription()}</p>
      </div>
      {renderTabContent()}
    </div>
  );
};

export default ArticlesSection;
